import React, { useEffect, useState } from "react";

import { Package, Plus, Search, Pencil, Eye, Trash2 } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import { DataTable } from "@/components/ui/table";
import TablePagination from "@/components/common/TablePagination";
import {
  deleteBaseModule,
  getBaseModules,
  getBaseModuleTypeDropdown,
} from "@/redux/Modules/baseModules/actions";
import TableText from "@/components/common/TableText";
import { useNavigate } from "react-router-dom";
import AddBaseModuleModal from "../modals/AddBaseModuleModal";
import ConfirmModal from "@/components/ui/CelebrationButton/ConfirmModal/ConfirmModal";
import { showToast } from "@/redux/Toast/actions";
import { SelectInput } from "../components/FormControls";

const BaseModulesPage = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { loading, baseModulesList, pagination, moduleTypeDropdown } =
    useSelector((state) => state.baseModules);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ======================================================
  // COLUMNS
  // ======================================================

  const columns = [
    // {
    //   key: "BASEMODULE_SEQUENCE_NO",
    //   label: "#",

    //   render: (row) => (
    //     <span className="font-medium text-slate-700 dark:text-slate-300">
    //       {row.BASEMODULE_SEQUENCE_NO}
    //     </span>
    //   ),
    // },

    {
      key: "BASEMODULE_NAME",
      label: "Base Module Name",
      width: "240px",
      render: (row) => (
        <div
          onClick={() => handleViewModules(row)}
          className="
        flex items-center gap-3 min-w-0
        cursor-pointer
        rounded-lg
        p-2
        -m-2
        transition-all
        hover:bg-slate-50
        dark:hover:bg-slate-800
      "
        >
          <div className="w-10 h-10 shrink-0 rounded-xl bg-teal-500/10 flex items-center justify-center">
            <Package className="w-5 h-5 text-teal-500" />
          </div>

          <div className="min-w-0 w-0 flex-1">
            <TableText
              title={row.BASEMODULE_NAME}
              subtitle={row.BASEMODULE_DESCRIPTION || "—"}
              multiline
              maxLines={2}
            />
          </div>
        </div>
      ),
    },

    {
      key: "BASEMODULE_TYPE",
      label: "Type",

      render: (row) => (
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            row.BASEMODULE_TYPE === "CORE"
              ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
              : "bg-purple-500/10 text-purple-600 dark:text-purple-400"
          }`}
        >
          {row.BASEMODULE_TYPE}
        </span>
      ),
    },

    {
      key: "BASEMODULE_BASEPRICE",
      label: "Base Price",

      render: (row) => (
        <div className="font-medium text-slate-900 dark:text-white">
          ${row.BASEMODULE_BASEPRICE}
        </div>
      ),
    },

    {
      key: "BASEMODULE_BASEPRICETYPE",
      label: "Pricing Type",
      width: 120,

      render: (row) => (
        <span className="text-slate-600 dark:text-slate-300">
          {row.BASEMODULE_BASEPRICETYPE}
        </span>
      ),
    },

    {
      key: "BASEMODULE_TRIAL_DAYS",
      label: "Trial",
      width: 100,

      render: (row) => (
        <span className="text-slate-700 dark:text-slate-300">
          {row.BASEMODULE_TRIAL_DAYS} Days
        </span>
      ),
    },

    {
      key: "modulesCount",
      label: "Modules",
      width: 100,

      render: (row) => (
        <span className="font-medium text-slate-900 dark:text-white">
          {row?.MODULES_DATA?.length || 0}
        </span>
      ),
    },

    {
      key: "BASEMODULE_IS_ACTIVE",
      label: "Status",
      width: 100,
      render: (row) => (
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            row.BASEMODULE_IS_ACTIVE
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-red-500/10 text-red-600 dark:text-red-400"
          }`}
        >
          {row.BASEMODULE_IS_ACTIVE ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      key: "actions",
      label: "Actions",

      render: (row) => (
        <div className="flex items-center gap-2">
          {/* View Modules */}

          <button
            onClick={() => handleViewModules(row)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="View Modules"
          >
            <Eye className="w-4 h-4 text-slate-600 dark:text-slate-300" />
          </button>

          {/* Edit */}

          <button
            onClick={() => handleEdit(row)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4 text-blue-500" />
          </button>

          {/* Toggle Status */}

          <button
            onClick={() => handleDeleteClick(row)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Toggle Status"
          >
            <Trash2 className="w-4 h-4 text-amber-500" />
          </button>
        </div>
      ),
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((col) => col.key),
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedModuleType, setSelectedModuleType] = useState("");

  // ======================================================
  // FETCH
  // ======================================================

  const fetchBaseModules = async () => {
    try {
      const requestPayload = {
        iPageNo: currentPage,
        iPageRecords: pageSize,
        SearchKeyword: debouncedSearch,
        ModuleType: selectedModuleType,
      };

      dispatch(getBaseModules(requestPayload));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBaseModules();
  }, [currentPage, pageSize, debouncedSearch, selectedModuleType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed === "" || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!moduleTypeDropdown?.length) {
      dispatch(getBaseModuleTypeDropdown());
    }
  }, [dispatch, moduleTypeDropdown]);

  // ======================================================
  // HANDLERS
  // ======================================================

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleViewModules = (row) => {
    navigate(`/admin/base_modules/${row.BASEMODULE_ID}/modules`);
  };

  const handleAdd = () => {
    setIsEdit(false);
    setSelectedModule(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    setIsEdit(true);
    setSelectedModule(row);
    setModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setSelectedModule(row);
    setConfirmOpen(true);
  };

  const handleClose = (open) => {
    setModalOpen(open);

    if (!open) {
      setSelectedModule(null);
      setIsEdit(false);
    }
  };

  const handleConfirmAction = async () => {
    try {
      const payload = {
        BASEMODULE_ID: selectedModule?.BASEMODULE_ID,
        DELETED_BY: "admin", // replace with logged-in user
      };

      await dispatch(deleteBaseModule(payload));

      dispatch(showToast("Base module deleted successfully", "success", true));

      setConfirmOpen(false);

      // refresh list if needed
      // dispatch(getSubModules(...));
    } catch (error) {
      dispatch(
        showToast(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to delete Base Module",
          "error",
          true,
        ),
      );
    }
  };

  // ======================================================
  // FILTERED COLUMNS
  // ======================================================

  const filteredColumns = columns.filter(
    (column) =>
      visibleColumns.length === 0 || visibleColumns.includes(column.key),
  );

  // ======================================================
  // RETURN
  // ======================================================

  return (
    <>
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
        <div className="space-y-4">
          {/* ================================================= */}
          {/* HEADER */}
          {/* ================================================= */}

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Package className="w-7 h-7 text-teal-500" />
                Base Modules
              </h1>

              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Manage base modules and pricing structure
              </p>
            </div>

            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Base Module
            </button>
          </div>

          {/* ================================================= */}
          {/* SEARCH */}
          {/* ================================================= */}

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative w-full md:w-96">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  placeholder="Search base module..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
                <span className="font-bold text-slate-900 dark:text-white">
                  {pagination?.totalRecord ?? 0} module found
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <SelectInput
                name="MODULE_TYPE"
                value={selectedModuleType}
                onValueChange={(name, value) => setSelectedModuleType(value)}
                placeholder="All Module Types"
                className="!py-2"
                options={moduleTypeDropdown?.map((item) => ({
                  value: item.moduleTypeValue,
                  label: item.moduleType,
                }))}
              />

              <ColumnVisibilityDropdown
                columns={columns}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                storageKey="base-modules-table-columns"
              />
            </div>
          </div>

          {/* ================================================= */}
          {/* TABLE */}
          {/* ================================================= */}

          <DataTable
            data={baseModulesList}
            columns={filteredColumns}
            loading={loading}
            emptyText="No Base Modules found"
            Icon={Package}
          />

          {/* ================================================= */}
          {/* PAGINATION */}
          {/* ================================================= */}

          <div className="p-4 pt-0">
            <TablePagination
              currentPage={currentPage}
              totalPages={pagination?.totalPage || 1}
              totalRecords={pagination?.totalRecord || 0}
              currentRecord={pagination?.currentRecord || 0}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      </div>
      {/* MODAL */}

      <AddBaseModuleModal
        open={modalOpen}
        onOpenChange={handleClose}
        onSuccess={fetchBaseModules}
        isEdit={isEdit}
        editData={selectedModule}
      />

      <ConfirmModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmAction}
        confirmText="Delete"
        title="Delete Base Module"
        message={
          <>
            Are you sure you want to delete{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {selectedModule?.BASEMODULE_NAME} ?
            </span>
          </>
        }
      />
    </>
  );
};

export default BaseModulesPage;
